import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Space,
  message,
  Spin,
} from 'antd';
import {
  ArrowLeftOutlined,
  EyeOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import userService from '../../services/user';
import getImage from '../../helpers/getImage';
import numberToPrice from '../../helpers/numberToPrice';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import UserTransactionsModal from './user-transactions-modal';
import axios from 'axios';

export default function UserDetails() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { defaultCurrency } = useSelector(
    (state) => state.currency,
    shallowEqual
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transactionsModalVisible, setTransactionsModalVisible] =
    useState(false);

  function fetchUserDetails() {
    setLoading(true);
    userService
      .getUserDetails(uuid)
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        message.error(
          err.response?.data?.message || 'Failed to fetch user details'
        );
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (uuid) {
      fetchUserDetails();
    }
  }, [uuid]);

  const handleBlockUser = async (currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = currentStatus === true ? 1 : 0;

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/block-user/${uuid}/${newStatus}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.status) {
        message.success(
          newStatus === 1
            ? 'User blocked successfully'
            : 'User activated successfully'
        );
        fetchUserDetails();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        message.error('Please login to continue');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Something went wrong!');
      }
    }
  };

  const transactionColumns = [
    {
      title: t('transaction.id'),
      dataIndex: 'givzo_ref_no',
      key: 'givzo_ref_no',
    },
    {
      title: t('order.id'),
      dataIndex: 'givzo_order_id',
      key: 'givzo_order_id',
    },
    {
      title: t('type'),
      dataIndex: 'payable_type',
      key: 'payable_type',
    },
    {
      title: t('amount'),
      dataIndex: 'price',
      key: 'price',
      render: (price) => numberToPrice(price / 100, '₹'),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          SUCCESS: 'green',
          FAILED: 'red',
          PENDING: 'orange',
          REFUNDED: 'blue',
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: t('date'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size='large' />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { user, addresses, stats, recent_transactions } = data;

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/users')}
        style={{ marginBottom: 16 }}
      >
        {t('back.to.users')}
      </Button>

      <Row gutter={[16, 16]}>
        {/* User Profile Card */}
        <Col span={24}>
          <Card
            title={t('user.profile')}
            extra={
              <Tag
                color={!user.blocked ? 'success' : 'error'}
                icon={
                  !user.blocked ? <CheckCircleOutlined /> : <StopOutlined />
                }
                style={{ cursor: 'pointer', padding: '4px 12px' }}
                onClick={() => handleBlockUser(!user.blocked)}
              >
                {!user.blocked ? t('active') : t('blocked')}
              </Tag>
            }
          >
            <Row gutter={[24, 24]}>
              <Col span={6}>
                <img
                  src={getImage(user.img)}
                  alt={user.firstname}
                  style={{ width: '100%', borderRadius: '8px' }}
                />
              </Col>
              <Col span={18}>
                <Descriptions column={2} bordered>
                  <Descriptions.Item label={t('user.id')}>
                    {user.id}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('user.type')}>
                    {user.user_type}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('name')} span={2}>
                    {user.firstname} {user.lastname}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('email')}>
                    {user.email}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('phone')}>
                    {user.phone}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('joined.on')}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('last.login')}>
                    {user.last_login_at
                      ? new Date(user.last_login_at).toLocaleString()
                      : t('never')}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Statistics Cards */}
        <Col span={8}>
          <Card>
            <Statistic
              title={t('gift.card.purchases')}
              value={stats.gift_card_count}
              suffix={t('cards')}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('gift.card.total.spent')}
              value={stats.gift_card_total_spent / 100}
              prefix='₹'
              precision={2}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('bill.payment.total.spent')}
              value={stats.bill_payment_total_spent / 100}
              prefix='₹'
              precision={2}
            />
          </Card>
        </Col>

        {/* Addresses Card */}
        {/* <Col span={24}>
          <Card title={t('addresses')}>
            {addresses && addresses.length > 0 ? (
              <Row gutter={[16, 16]}>
                {addresses.map((address) => (
                  <Col span={8} key={address.id}>
                    <Card size="small" title={address.title}>
                      <p>{address.address}</p>
                      {address.location && (
                        <p style={{ fontSize: '12px', color: '#888' }}>
                          Lat: {address.location.lat}, Lng: {address.location.lng}
                        </p>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <p style={{ color: '#888' }}>{t('no.addresses.found')}</p>
            )}
          </Card>
        </Col> */}

        {/* Recent Transactions */}
        <Col span={24}>
          <Card
            title={t('recent.transactions')}
            extra={
              <Button
                type='primary'
                icon={<EyeOutlined />}
                onClick={() => setTransactionsModalVisible(true)}
              >
                {t('view.all')}
              </Button>
            }
          >
            <Table
              columns={transactionColumns}
              dataSource={recent_transactions}
              rowKey='id'
              pagination={false}
              size='small'
            />
          </Card>
        </Col>
      </Row>

      <UserTransactionsModal
        uuid={uuid}
        visible={transactionsModalVisible}
        handleCancel={() => setTransactionsModalVisible(false)}
      />
    </div>
  );
}
